import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import moment from "moment";
import { CircularProgress } from "@mui/material";

import PrimaryButton from "../../../Components/Buttons/primary-button";

import renderMailCheckOnTheWay from "./mail-check-on-the-way";

import { useRequestCheck } from "../../../CustomHooks/Mutations";

import { useModalContext } from "../../../Contexts/ModalContext";

import { dispatchErrorToast, formatCurrency, getUserId } from "../../../Utils/helpers";
import { APIQueryKeys, modalVariants } from "../../../Utils/constants";

import { buildAddressLines, toCheckRequestAddress } from "../../../Pages/Cash Transaction Pages/mail-check-utils";

import "./mail-check.scss";

const SHORT_DATE_FORMAT = "MMM D";

export const getCheckArrivalWindow = () => {
  const start = moment().add(7, "days");
  const end = moment().add(10, "days");
  return {
    start,
    end,
    label: `${start.format(SHORT_DATE_FORMAT)} – ${end.format(SHORT_DATE_FORMAT)}`,
    labelWithYear: `${start.format(SHORT_DATE_FORMAT)} – ${end.format("MMM D, YYYY")}`,
  };
};

export default function renderAuthorizeMailCheck(modalContext, props) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalClassName, setModalTitle } = modalContext;

  updateModalComponent(() => <AuthorizeMailCheck {...props} />);
  pushModalStack(() => renderAuthorizeMailCheck(modalContext, props));
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("mail-check-modal authorize-mail-check-modal align-title-left hide-back-button");
  setModalTitle("Authorize this transaction");
}

const AuthorizeMailCheck = ({ cashAmount, mailingAddress, checkFee, isFeeCharged }) => {
  const modalContext = useModalContext();
  const queryClient = useQueryClient();

  const [agreed, setAgreed] = useState(false);

  const arrival = useMemo(() => getCheckArrivalWindow(), []);

  const recipientName = `${mailingAddress?.firstName ?? ""} ${mailingAddress?.lastName ?? ""}`.trim();
  const addressLines = useMemo(() => buildAddressLines(mailingAddress), [mailingAddress]);
  const netCheckAmount = isFeeCharged ? cashAmount - checkFee : cashAmount;

  const { isLoading: isRequestingCheck, mutate: requestCheck } = useRequestCheck({
    onSuccess: async ({ data }) => {
      await queryClient.refetchQueries(APIQueryKeys.checkConfig);
      renderMailCheckOnTheWay(modalContext, { cashAmount, mailingAddress, arrival, checkResponse: data });
    },
    onError: (err) =>
      dispatchErrorToast(err?.response?.data?.error ?? "Unable to submit your check request. Please try again."),
  });

  const onAuthorize = () => {
    if (!agreed || isRequestingCheck) return;
    requestCheck({ mobileUserId: getUserId(), amount: cashAmount, address: toCheckRequestAddress(mailingAddress) });
  };

  return (
    <div className="modal-dialog-content amc-content">
      <div className="amc-amount-hero">
        <span className="amc-amount-label">Amount</span>
        <span className="amc-amount">{formatCurrency(netCheckAmount)}</span>
        <span className="amc-amount-desc">Arrives {arrival.label} · 7–10 business days</span>
      </div>

      <div className="amc-transfer-card">
        <div className="amc-card-row amc-card-row-border">
          <span className="amc-card-label">From</span>
          <div className="amc-card-entity">
            <span className="amc-card-value">Stake</span>
            <span className="amc-card-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v4M12 14v4M16 14v4" fill="none" stroke="#F4FDFA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </div>
        <div className="amc-card-row">
          <span className="amc-card-label">To</span>
          <div className="amc-card-entity">
            <span className="amc-card-value">Paper check</span>
            <span className="amc-card-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" fill="none" stroke="#F4FDFA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="2" y1="10" x2="22" y2="10" stroke="#F4FDFA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="6" y1="14" x2="12" y2="14" stroke="#F4FDFA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      <div className="amc-details-section">
        <div className="amc-detail-row amc-detail-row-border amc-detail-row-top">
          <span className="amc-detail-label">Mailing Address</span>
          <span className="amc-detail-value amc-detail-value-multiline">
            {recipientName}
            {addressLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </span>
        </div>
        <div className="amc-detail-row amc-detail-row-border">
          <span className="amc-detail-label">Payment Method</span>
          <span className="amc-detail-value">USPS mailed check</span>
        </div>
        <div className="amc-detail-row amc-detail-row-border">
          <span className="amc-detail-label">Expected Arrival</span>
          <span className="amc-detail-value">{arrival.label}</span>
        </div>
        <div className="amc-detail-row">
          <span className="amc-detail-label">Fees</span>
          <span className={`amc-detail-value ${isFeeCharged ? "amc-fee-charged" : "amc-fee-free"}`}>
            {isFeeCharged ? formatCurrency(checkFee) : "Free"}
          </span>
        </div>
      </div>

      <div className="amc-footer">
        <p className="amc-disclaimer">
          *Checks are processed within 1 business day and mailed via USPS. Once mailed, the destination address cannot be
          changed.
        </p>
        <label className="amc-consent-row">
          <button
            type="button"
            className={`amc-checkbox${agreed ? " checked" : ""}`}
            onClick={() => setAgreed((prev) => !prev)}
            aria-pressed={agreed}
            aria-label="Authorize this transaction"
          >
            <svg viewBox="0 0 12 12" fill="none">
              <path d="M2 6L5 9L10 3" stroke="#0D0F14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="amc-consent-text">
            By selecting &ldquo;Authorize,&rdquo; you instruct Stake to debit {formatCurrency(cashAmount)} from your Stake
            Wallet and mail a physical check to the address shown above. You confirm that the mailing address is current
            and that this transaction cannot be reversed once the check is mailed.
          </span>
        </label>
        <PrimaryButton className="amc-authorize-btn" onClick={onAuthorize} disabled={!agreed || isRequestingCheck}>
          Authorize {isRequestingCheck && <CircularProgress />}
        </PrimaryButton>
      </div>
    </div>
  );
};
